import { C1AutocompleteMultipleAsyncHook, C1AutocompleteSingleAsyncHook, Searchable } from 'components/core/autocomplete'
import { APIError } from 'components/core/errors/api'
import { Policy, SearchPoliciesRequest, PolicyType, PolicyRef, GetPolicyRequest } from 'pbts/c1/api/policy/v1/policy'
import React, { ComponentProps, ReactNode, useEffect } from 'react'
import { getPolicyApi, searchPoliciesApi } from 'redux_toolkit/policies'

const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, policy: Policy): ReactNode => (
    <li {...props} key={policy.id}>
        {policy.displayName}
    </li>
)

const renderLabel = (policy: Searchable): string => {
    return policy.displayName || 'unknown'
}

type SearchPolicyProps = Omit<
    ComponentProps<typeof C1AutocompleteSingleAsyncHook<SearchPoliciesRequest, Policy>>,
    'Req' | 'hook' | 'renderOption' | 'renderLabel' | 'initialValue' | 'type'
> & {
    policy?: Policy
    onSelected: (policy: Policy) => void
    policyType: PolicyType
}

export const SearchPolicies: React.FC<SearchPolicyProps> = ({ policy, policyType, ...rest }) => {
    const req = {
        create: (q: Partial<SearchPoliciesRequest>): SearchPoliciesRequest => SearchPoliciesRequest.create({
            ...q,
            policyTypes: [policyType],
        }),
    }

    return <C1AutocompleteSingleAsyncHook<SearchPoliciesRequest, Policy>
        {...rest}
        hook={searchPoliciesApi.useLazySearch}
        Req={req}
        renderOption={renderOption}
        renderLabel={renderLabel}
        initialValue={policy}
        type={Policy}
    />
}

type SearchPoliciesFromIdProps = {
    id?: string
    onSelected: (id: string) => void
} & Omit<SearchPolicyProps, 'policy'>

export const SearchPoliciesFromId: React.FC<SearchPoliciesFromIdProps> = ({
    id, onSelected: _onSelected, ...rest
}) => {
    const { data, trigger, error } = getPolicyApi.useLazyGet()

    useEffect(() => {
        if (id) trigger(GetPolicyRequest.create({ id }))
    }, [id, trigger])

    const onSelected = (policy: Policy | null): void => {
        if (policy) _onSelected(policy.id)
    }

    if (error) return <APIError err={error} />

    return <SearchPolicies policy={data} onSelected={onSelected} {...rest} />
}

export type MultiSearchPoliciesProps = Omit<
    ComponentProps<typeof C1AutocompleteMultipleAsyncHook<SearchPoliciesRequest, Policy>>,
    'Req' | 'hook' | 'renderOption' | 'renderLabel' | 'initialValue'
> & {
    baseRequest?: Partial<SearchPoliciesRequest>
    policies?: Policy[]
    onSelected: (policies: Policy[]) => void
}

export const MultiSearchPolicies: React.FC<MultiSearchPoliciesProps> = ({ policies, baseRequest, ...rest }) => {
    const req = {
        create: (q: Partial<SearchPoliciesRequest>): SearchPoliciesRequest => SearchPoliciesRequest.create({ ...q, ...baseRequest }),
    }

    return <C1AutocompleteMultipleAsyncHook<SearchPoliciesRequest, Policy>
        {...rest}
        hook={searchPoliciesApi.useLazySearch}
        Req={req}
        renderOption={renderOption}
        renderLabel={renderLabel}
        initialValue={policies}
    />
}

type MultiSearchPoliciesFromIdsProps = {
    ids: (string | undefined)[] | undefined
    onSelected: (ids: string[]) => void
} & Omit<MultiSearchPoliciesProps, 'policies'>

export const MultiSearchPoliciesFromIds: React.FC<MultiSearchPoliciesFromIdsProps> = ({
    ids,
    onSelected: _onSelected,
    baseRequest,
    ...rest
}) => {
    const refs = ids ? ids.map((id) => PolicyRef.create({ id })) : []

    const { data, trigger, error } = searchPoliciesApi.useLazySearch()

    useEffect(() => {
        if (refs.length > 0) trigger(SearchPoliciesRequest.create({ ...baseRequest, refs }))
    }, [trigger]) // eslint-disable-line react-hooks/exhaustive-deps

    const onSelected = (policies: Policy[]): void => {
        _onSelected(policies.map((policy) => policy.id))
    }

    if (error) return <APIError err={error} />

    return <MultiSearchPolicies policies={data[0] as Policy[]} onSelected={onSelected} baseRequest={baseRequest} {...rest} />
}
